// Binds the App Bridge session-token retrieval to the API client and exposes it
// through React context. Any component below can call `useApi()` to get typed,
// authenticated endpoint methods without ever touching App Bridge directly.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import { getSessionToken } from "@shopify/app-bridge/utilities";
import { createApiClient, type ApiClient } from "./client";
import { createMockApiClient } from "./mockData";

const ApiContext = createContext<ApiClient | null>(null);

// Preview mode: provide a mock client (built-in sample data, no App Bridge, no
// backend) through the SAME context, so every page below works unchanged.
export function MockApiProvider({ children }: { children: ReactNode }) {
  const client = useMemo(() => createMockApiClient(), []);
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const app = useAppBridge();

  // Rebuild the client only when the App Bridge instance changes. `getToken`
  // asks App Bridge for a fresh, short-lived session token on every request.
  const client = useMemo(() => createApiClient(() => getSessionToken(app)), [app]);

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi must be used within <ApiProvider>");
  }
  return client;
}
